"use client";

import React from "react";
import styles from "./gateways-status.module.scss";
import { HealthResponse } from "@/types/health.types";
import { ServiceRowBadge, CountBadge } from "../components";
import { formatBalance, formatEthBalance, truncateAddress, getChainName, createScrollHandler } from "../utils";

interface GatewaysStatusProps {
  healthData: HealthResponse;
}

const GatewaysStatus: React.FC<GatewaysStatusProps> = ({ healthData }) => {


  const getGatewayCounts = () => {
    const balances: { healthy: boolean }[] = [];

    // token gateway balances
    if (healthData.tokenGateways.available) {
      healthData.tokenGateways.gateways.forEach(gateway => {
        balances.push(
          { healthy: gateway.ethHealthy },
          { healthy: gateway.blessHealthy }
        );
      });
    }

    // transaction gateway balances
    if (healthData.transactionGateway.available) {
      balances.push(
        { healthy: healthData.transactionGateway.ethHealthy },
        { healthy: healthData.transactionGateway.blessHealthy }
      );
    }

    const healthyCount = balances.filter(b => b.healthy).length;
    const unhealthyCount = balances.filter(b => !b.healthy).length;

    return { healthyCount, unhealthyCount, totalCount: balances.length };
  };

  const scrollToGatewaysContent = createScrollHandler('gateways-content');

  return (
    <div id="gateways-status" className={styles.card}>
      <div className={styles.header}>
        <h3>Gateway Balances</h3>
        <div className={styles.summary}>
          {getGatewayCounts().healthyCount > 0 && (
            <CountBadge
              count={getGatewayCounts().healthyCount}
              label="Healthy"
              isHealthy={true}
              onClick={scrollToGatewaysContent}
            />
          )}
          {getGatewayCounts().unhealthyCount > 0 && (
            <CountBadge
              count={getGatewayCounts().unhealthyCount}
              label="Unhealthy"
              isHealthy={false}
              onClick={scrollToGatewaysContent}
            />
          )}
        </div>
      </div>

      <div id="gateways-content" className={styles.content}>
        <div className={styles.balanceGrid}>
          {/* token gateway Balances */}
          {healthData.tokenGateways?.available &&
            healthData.tokenGateways.gateways.map((gateway, index) => (
              <React.Fragment key={gateway.address}>
                <ServiceRowBadge
                  isHealthy={gateway.ethHealthy}
                  serviceName={`token gateway #${index + 1} ${truncateAddress(gateway.address)}`}
                  chainId={healthData.tokenGateways.parentChainId}
                  metrics={[
                    {
                      label: "ETH",
                      value: formatEthBalance(gateway.ethBalance)
                    }
                  ]}
                />
                <ServiceRowBadge
                  isHealthy={gateway.blessHealthy}
                  serviceName={`token gateway #${index + 1} ${truncateAddress(gateway.address)}`}
                  chainId={healthData.tokenGateways.parentChainId}
                  metrics={[
                    {
                      label: "BLESS",
                      value: formatBalance(gateway.blessBalance)
                    }
                  ]}
                />
              </React.Fragment>
            ))
          }

          {/* transaction gateway Balances */}
          {healthData.transactionGateway?.available && (
            <>
              <ServiceRowBadge
                isHealthy={healthData.transactionGateway.ethHealthy}
                serviceName={`transaction gateway ${truncateAddress(healthData.transactionGateway.gateway)}`}
                chainId={healthData.transactionGateway.parentChainId}
                metrics={[
                  {
                    label: "ETH",
                    value: formatEthBalance(healthData.transactionGateway.ethBalance)
                  }
                ]}
              />
              <ServiceRowBadge
                isHealthy={healthData.transactionGateway.blessHealthy}
                serviceName={`transaction gateway ${truncateAddress(healthData.transactionGateway.gateway)}`}
                chainId={healthData.tokenGateways.parentChainId}
                metrics={[
                  {
                    label: "BLESS",
                    value: formatBalance(healthData.transactionGateway.blessBalance)
                  }
                ]}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GatewaysStatus;
